class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this)); 
    this.setHeaderCartIconAccessibility();
    this.cartRefreshEvents = ['cart:refresh', 'cart:build', 'cart:updated'];
    this.onExternalCartUpdate = () => this.refresh();
    this.cartRefreshVersion = 0;
  }

  connectedCallback() {
    this.cartRefreshEvents.forEach((eventName) => document.addEventListener(eventName, this.onExternalCartUpdate));
  }

  disconnectedCallback() {
    this.cartRefreshEvents.forEach((eventName) => document.removeEventListener(eventName, this.onExternalCartUpdate));
    clearTimeout(this.cartRefreshTimer);
    this.cartRefreshController?.abort();
    this.cartRefreshVersion++;
  }
  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});
    setTimeout(() => {if(this.querySelector('subcart-drawer')) this.querySelector('subcart-drawer').openSubDrawer()});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if(this.querySelector('subcart-drawer')) this.querySelector('subcart-drawer').closeSubDrawer();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    // Add responses can precede BundleX companion lines. Always read the current cart.
    this.refresh(true);
  }

  refresh(openDrawer = false) {
    this.openAfterRefresh = this.openAfterRefresh || openDrawer;
    const version = ++this.cartRefreshVersion;
    clearTimeout(this.cartRefreshTimer);
    this.cartRefreshController?.abort();
    // BundleX emits three events together; fetch their sections only once.
    this.cartRefreshTimer = setTimeout(() => this.refreshContents(version), 50);
  }

  async refreshContents(version) {
    const controller = new AbortController();
    this.cartRefreshController = controller;
    try {
      const url = new URL(routes.cart_url, window.location.origin);
      url.searchParams.set('sections', this.getSectionsToRender().map((section) => section.id).join(','));
      const response = await fetch(url.toString(), { signal: controller.signal, cache: 'no-store' });
      if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
      const sections = await response.json();
      if (version !== this.cartRefreshVersion || !this.isConnected) return;

      const drawer = this.getSectionDOM(sections['cart-drawer'], 'cart-drawer');
      const bubble = this.getSectionDOM(sections['cart-icon-bubble']);
      if (!drawer?.querySelector('#CartDrawer') || !bubble) throw new Error('Cart refresh sections are missing');

      const activeElementId = this.contains(document.activeElement) ? document.activeElement.id : null;
      this.querySelector('#CartDrawer').innerHTML = drawer.querySelector('#CartDrawer').innerHTML;
      this.classList.toggle('is-empty', drawer.classList.contains('is-empty'));
      document.getElementById('cart-icon-bubble').innerHTML = bubble.innerHTML;
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

      if (this.openAfterRefresh) {
        this.openAfterRefresh = false;
        this.open();
      }
      if (this.classList.contains('active')) {
        const focusElement = (activeElementId && this.querySelector(`#${CSS.escape(activeElementId)}`)) || this.querySelector('.drawer__close');
        trapFocus(this.querySelector('#CartDrawer'), focusElement);
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class SubCartDrawer extends HTMLElement {
  constructor() {
    super();
  }

   closeSubDrawer(){
    this.classList.remove('active')
  }

  openSubDrawer(){
    this.classList.add('animate', 'active')
  }
}

customElements.define('subcart-drawer', SubCartDrawer);
